export default function PieceBasedPricingGuide() {
	return (
		<div className="mx-auto max-w-4xl px-6 py-8">
			<div className="prose prose-blue max-w-none">
				<h1 className="text-3xl font-bold text-gray-900 mb-6">Piece-Based Pricing Guide</h1>
				<p className="mb-4 text-gray-600">
					This guide explains how to set up and use piece-based pricing for ingredients that are counted as individual items (slices, pieces, wraps, etc.) rather than measured by weight.
				</p>

				<h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">Overview</h2>
				<p className="mb-4">
					Piece-based pricing allows you to cost recipes based on individual pieces rather than weight measurements.
					For example, "2 slices of bacon" or "1 tortilla wrap" instead of "60g bacon" or "45g tortilla".
				</p>

				<h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">When to Use Piece-Based Pricing</h2>
				<p className="mb-4">Use piece-based pricing for ingredients that are:</p>
				<ul className="list-disc list-inside space-y-1 mb-4">
					<li><strong>Naturally portioned</strong>: Bacon slices, chicken breasts, burger buns</li>
					<li><strong>Pre-cut items</strong>: Sliced cheese, deli meats, pizza slices</li>
					<li><strong>Countable items</strong>: Tortilla wraps, eggs, bread rolls</li>
					<li><strong>Uniform portions</strong>: Items where each piece has consistent size/weight</li>
				</ul>

				<h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">Setting Up Inventory Items</h2>

				<h3 className="text-xl font-medium text-gray-700 mt-6 mb-3">Step 1: Basic Information</h3>
				<p className="mb-4">Set up your inventory item normally with:</p>
				<ul className="list-disc list-inside space-y-1 mb-4">
					<li><strong>Ingredient Name</strong>: Description of the item</li>
					<li><strong>Price per base unit</strong>: Cost per gram/ml derived from the container price</li>
					<li><strong>Container Size</strong>: Total weight or volume of the package</li>
					<li><strong>Case Information</strong>: Case size and price if applicable</li>
				</ul>

				<h3 className="text-xl font-medium text-gray-700 mt-6 mb-3">Step 2: Portion Information</h3>
				<p className="mb-4">This is where piece-based pricing is configured:</p>

				<h4 className="text-lg font-medium text-gray-700 mt-4 mb-2">Piece Size &amp; Unit</h4>
				<p className="mb-4">Enter the weight or volume of <strong>one piece</strong> and choose the unit that matches how you measure it (grams, ounces, milliliters, etc.).</p>
				<p className="mb-4"><strong>Examples:</strong></p>
				<ul className="list-disc list-inside space-y-1 mb-4">
					<li>Bacon slice: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">28 g</code></li>
					<li>Tortilla wrap: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">1.6 oz</code></li>
					<li>Cheese slice: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">0.7 oz</code></li>
					<li>Chicken breast: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">200 g</code></li>
				</ul>

				<h4 className="text-lg font-medium text-gray-700 mt-4 mb-2">Piece Measure</h4>
				<p className="mb-4">Select the unit type from dropdown:</p>
				<ul className="list-disc list-inside space-y-1 mb-4">
					<li><code className="bg-gray-100 px-1 py-0.5 rounded text-sm">slice</code> - for sliced items (bacon, cheese, deli meat)</li>
					<li><code className="bg-gray-100 px-1 py-0.5 rounded text-sm">piece</code> - for individual items (wraps, buns, chicken pieces)</li>
					<li><code className="bg-gray-100 px-1 py-0.5 rounded text-sm">each</code> - alternative to piece</li>
				</ul>

				<h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">Using in Recipes</h2>

				<h3 className="text-xl font-medium text-gray-700 mt-6 mb-3">Recipe Entry</h3>
				<p className="mb-4">When adding the ingredient to a recipe:</p>
				<ol className="list-decimal list-inside space-y-1 mb-4">
					<li><strong>Amount</strong>: Enter the number of pieces needed (Example: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">2</code> for 2 slices of bacon)</li>
					<li><strong>Unit</strong>: Must match the Piece Measure from inventory</li>
				</ol>

				<h3 className="text-xl font-medium text-gray-700 mt-6 mb-3">Cost Calculation</h3>
				<p className="mb-4">The system automatically calculates:</p>
				<pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto mb-4">
					<code>Recipe Cost = Amount × (Price per gram × Piece Size)</code>
				</pre>
				<p className="mb-4"><strong>Example - Bacon:</strong></p>
				<p className="mb-4">2 slices × ($0.0135/gram × 28 grams/slice) = 2 × $0.378 = $0.76</p>

				<h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">Examples</h2>

				<h3 className="text-xl font-medium text-gray-700 mt-6 mb-3">Example 1: Bacon Slices</h3>
				<p className="mb-4"><strong>Inventory Setup:</strong></p>
				<ul className="list-disc list-inside space-y-1 mb-4">
					<li>Piece Size: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">28</code> grams</li>
					<li>Piece Measure: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">slice</code></li>
					<li>Price per gram: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">$0.0135</code></li>
				</ul>
				<p className="mb-4"><strong>Recipe Usage:</strong></p>
				<ul className="list-disc list-inside space-y-1 mb-4">
					<li>Amount: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">3</code></li>
					<li>Unit: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">slice</code></li>
					<li><strong>Cost</strong>: 3 × ($0.0135 × 28) = $1.13</li>
				</ul>

				<h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">Important Matching Rules</h2>
				<p className="mb-4">For piece-based pricing to work, the <strong>Piece Measure</strong> in inventory must match the <strong>Unit</strong> in your recipe:</p>

				<p className="mb-2"><strong>✅ Correct Matches:</strong></p>
				<ul className="list-disc list-inside space-y-1 mb-4">
					<li>Inventory: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">slice</code> → Recipe: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">slice</code></li>
					<li>Inventory: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">piece</code> → Recipe: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">piece</code></li>
				</ul>

				<p className="mb-2"><strong>❌ Won't Work:</strong></p>
				<ul className="list-disc list-inside space-y-1 mb-4">
					<li>Inventory: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">slice</code> → Recipe: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">piece</code></li>
					<li>Inventory: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">piece</code> → Recipe: <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">gram</code></li>
				</ul>

				<h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">Troubleshooting</h2>

				<h3 className="text-xl font-medium text-gray-700 mt-6 mb-3">Cost Too Low</h3>
				<ul className="list-disc list-inside space-y-1 mb-4">
					<li>Check if Piece Size is set correctly (not 1 gram by default)</li>
					<li>Verify Price per gram is accurate</li>
				</ul>

				<h3 className="text-xl font-medium text-gray-700 mt-6 mb-3">Cost Too High</h3>
				<ul className="list-disc list-inside space-y-1 mb-4">
					<li>Piece Size might be too large</li>
					<li>Check if using correct unit in recipe (slice vs piece)</li>
				</ul>

				<h3 className="text-xl font-medium text-gray-700 mt-6 mb-3">System Not Using Piece Pricing</h3>
				<ul className="list-disc list-inside space-y-1 mb-4">
					<li>Ensure Piece Measure matches recipe unit exactly</li>
					<li>Check browser console for pricing strategy messages</li>
					<li>Verify Piece Size is greater than 0</li>
				</ul>
			</div>
			<div className="mt-8 pt-4 border-t">
				<button
					onClick={() => window.history.back()}
					className="text-blue-600 hover:text-blue-800 font-medium"
				>
					← Back to previous page
				</button>
			</div>
		</div>
	)
}
